/* eslint-disable space-before-function-paren */
import { post, patch, get, put, destroy } from 'src/services/api';
import { buildUrl } from 'src/services/requests/request.utils';
import { LOG_EVENT_TYPE, createLogEvent } from 'src/logger/log-event';
import { LOG_LEVEL, logger } from 'src/logger/logger';
import { getRequestFailureDetails } from 'src/logger/request-failure-details';
import {
    getInvitationSendSafeRequestData,
    selectInvitationBatchResponseData,
} from 'src/utils/invite-user-response.utils';

const CODE_LOCATION = 'src/services/requests/users-and-permissions/invitations/invitations.js';
const INVITATIONS_ENDPOINT = '/invitations';
const LYT_INVITATIONS_ENDPOINT = '/invitations/lyt';

/**
 * API invitation endpoints expect jurisdiction IDs as strings, while UI models
 * often carry `{ id, name }` objects from team/request serializers.
 *
 * @param {Array<string|{ id?: string }>|undefined} jurisdictions
 * @returns {string[]}
 */
export const toInvitationJurisdictionIds = (jurisdictions) => {
    if (!Array.isArray(jurisdictions)) {
        return [];
    }

    return jurisdictions
        .map((jurisdiction) => (typeof jurisdiction === 'string' ? jurisdiction : jurisdiction?.id))
        .filter(Boolean);
};

const getServiceRejectionOutcome = (failureCategory) => {
    if (failureCategory === 'canceled') return 'canceled';
    if (failureCategory === 'http') return 'response_received';
    if (failureCategory === 'request') return 'request_rejected';
    return 'no_response';
};

const createInvitationServiceEvent = ({
    methodName,
    message,
    outcome,
    transactionId,
    requestData,
    responseData,
    transportData,
}) => createLogEvent({
    eventType: LOG_EVENT_TYPE.SERVICE,
    codeLocation: CODE_LOCATION,
    methodName,
    level: LOG_LEVEL.INFO,
    message,
    outcome,
    transactionId,
    attributes: {
        request_data: requestData,
        ...(responseData ? { response_data: responseData } : {}),
        ...(transportData ? { transport_data: transportData } : {}),
    },
});

const logInvitationServiceRejection = ({
    methodName,
    transactionId,
    requestData,
    error,
}) => {
    const failureDetails = getRequestFailureDetails(error);
    const outcome = getServiceRejectionOutcome(failureDetails.failure_category);

    logger.event(createInvitationServiceEvent({
        methodName,
        message: outcome === 'response_received'
            ? 'Invitation send service response received'
            : 'Invitation send service request did not return a response',
        outcome,
        transactionId,
        requestData,
        responseData: failureDetails.status_code !== undefined
            ? { status_code: failureDetails.status_code }
            : undefined,
        transportData: {
            failure_category: failureDetails.failure_category,
        },
    }));
};

const postInvitations = async ({
    endpoint,
    body,
    requestData,
    methodName,
    transactionId,
}) => {
    if (transactionId) {
        logger.event(createInvitationServiceEvent({
            methodName,
            message: 'Invitation send service request started',
            outcome: 'request_started',
            transactionId,
            requestData,
        }));
    }

    try {
        const response = await post(endpoint, body);

        if (transactionId) {
            logger.event(createInvitationServiceEvent({
                methodName,
                message: 'Invitation send service response received',
                outcome: 'response_received',
                transactionId,
                requestData,
                responseData: selectInvitationBatchResponseData(
                    response.data,
                    response.status,
                ),
            }));
        }

        return response;
    } catch (err) {
        if (transactionId) {
            logInvitationServiceRejection({
                methodName,
                transactionId,
                requestData,
                error: err,
            });
        }
        throw err.response?.data || err;
    }
};

/**
 * Send user invitations via POST /invitations.
 *
 * @param {Object} params
 * @param {string[]} params.recipients      - List of email addresses to invite
 * @param {string}   params.assigned_role   - Role to assign (e.g. 'traffic_viewer')
 * @param {string}   params.agency_id       - Agency the invitees will belong to
 * @param {string[]} [params.jurisdictions] - Optional jurisdiction IDs (for traffic_viewer)
 * @param {string}   [params.customized_message] - Optional personal message
 * @param {string}   [params.transactionId]  - Correlation id for service logging
 * @returns {Promise<Object>} - API response data
 */
export const sendInvitations = async ({
    recipients,
    assigned_role,
    agency_id,
    jurisdictions,
    customized_message,
    transactionId,
} = {}) => {
    const body = {
        recipients,
        assigned_role,
        agency_id,
    };

    if (jurisdictions?.length) {
        body.jurisdictions = jurisdictions;
    }
    if (customized_message) {
        body.customized_message = customized_message;
    }

    const requestData = {
        method: 'POST',
        endpoint: INVITATIONS_ENDPOINT,
        ...getInvitationSendSafeRequestData({
            recipients,
            assigned_role,
            agency_id,
            jurisdictions,
            customized_message,
        }),
    };

    const { data } = await postInvitations({
        endpoint: INVITATIONS_ENDPOINT,
        body,
        requestData,
        methodName: 'sendInvitations',
        transactionId,
    });

    return data;
};

/**
 * Send LYT internal team invitations via POST /invitations/lyt.
 *
 * Unlike sendInvitations, this does NOT include agency_id in the body
 * because LYT internal users are not associated with a customer agency.
 *
 * @param {Object} params
 * @param {string[]} params.recipients      - List of email addresses to invite
 * @param {string}   params.assigned_role   - Role to assign (e.g. 'lyt_admin')
 * @param {string}   [params.customized_message] - Optional personal message
 * @param {string}   [params.transactionId]  - Correlation id for service logging
 * @returns {Promise<Object>} - API response data
 */
export const sendLytInvitations = async ({
    recipients,
    assigned_role,
    customized_message,
    transactionId,
} = {}) => {
    const body = {
        recipients,
        assigned_role,
    };

    if (customized_message) {
        body.customized_message = customized_message;
    }

    const requestData = {
        method: 'POST',
        endpoint: LYT_INVITATIONS_ENDPOINT,
        ...getInvitationSendSafeRequestData({
            recipients,
            assigned_role,
            customized_message,
        }, { isLyt: true }),
    };

    const { data } = await postInvitations({
        endpoint: LYT_INVITATIONS_ENDPOINT,
        body,
        requestData,
        methodName: 'sendLytInvitations',
        transactionId,
    });

    return data;
};

/**
 * Build query params that identify a single invitation record.
 *
 * @param {Object} params
 * @param {string} params.invitee_email
 * @param {string} params.created_at_ts
 * @param {string} [params.agency_id]
 * @returns {URLSearchParams}
 */
const buildInvitationIdentityQuery = ({
    agency_id,
    invitee_email,
    created_at_ts,
} = {}) => {
    const queryParams = new URLSearchParams();

    if (agency_id) {
        queryParams.append('agency_id', agency_id);
    }
    queryParams.append('invitee_email', invitee_email);
    queryParams.append('created_at_ts', created_at_ts);

    return queryParams;
};

/**
 * Revoke an agency invitation via DELETE /invitations.
 *
 * The invitation is identified by query params: agency_id, invitee_email, created_at_ts.
 *
 * @param {Object} params
 * @param {string} params.agency_id       - Agency ID the invitation belongs to
 * @param {string} params.invitee_email   - Email of the invitee
 * @param {string} params.created_at_ts   - ISO timestamp when the invitation was created
 * @returns {Promise<Object>} - API response data
 */
export const revokeInvitation = async ({
    agency_id,
    invitee_email,
    created_at_ts,
} = {}) => {
    const queryParams = buildInvitationIdentityQuery({
        agency_id,
        invitee_email,
        created_at_ts,
    });
    const url = `${INVITATIONS_ENDPOINT}?${queryParams.toString()}`;

    try {
        const { data } = await destroy(url);
        return data;
    } catch (err) {
        throw err.response?.data || err;
    }
};

/**
 * Revoke an LYT internal invitation via DELETE /invitations/lyt.
 *
 * @param {Object} params
 * @param {string} params.invitee_email   - Email of the invitee
 * @param {string} params.created_at_ts   - ISO timestamp when the invitation was created
 * @returns {Promise<Object>} - API response data
 */
export const revokeLytInvitation = async ({
    invitee_email,
    created_at_ts,
} = {}) => {
    const queryParams = buildInvitationIdentityQuery({
        invitee_email,
        created_at_ts,
    });
    const url = `${LYT_INVITATIONS_ENDPOINT}?${queryParams.toString()}`;

    try {
        const { data } = await destroy(url);
        return data;
    } catch (err) {
        throw err.response?.data || err;
    }
};

/**
 * Resend an agency invitation via PUT /invitations/resend.
 *
 * The invitation is identified by query params: agency_id, invitee_email, created_at_ts.
 * The endpoint is rate limited per invitation identity (429 + Retry-After).
 *
 * @param {Object} params
 * @param {string} params.agency_id       - Agency ID the invitation belongs to
 * @param {string} params.invitee_email   - Email of the invitee
 * @param {string} params.created_at_ts   - ISO timestamp when the invitation was created
 * @returns {Promise<Object>} - API response data
 */
export const resendInvitation = async ({
    agency_id,
    invitee_email,
    created_at_ts,
} = {}) => {
    const queryParams = buildInvitationIdentityQuery({
        agency_id,
        invitee_email,
        created_at_ts,
    });
    const url = `${INVITATIONS_ENDPOINT}/resend?${queryParams.toString()}`;

    try {
        const { data } = await put(url);
        return data;
    } catch (err) {
        throw err.response?.data || err;
    }
};

/**
 * Resend an LYT internal invitation via PUT /invitations/lyt/resend.
 *
 * @param {Object} params
 * @param {string} params.invitee_email   - Email of the invitee
 * @param {string} params.created_at_ts   - ISO timestamp when the invitation was created
 * @returns {Promise<Object>} - API response data
 */
export const resendLytInvitation = async ({
    invitee_email,
    created_at_ts,
} = {}) => {
    const queryParams = buildInvitationIdentityQuery({
        invitee_email,
        created_at_ts,
    });
    const url = `${LYT_INVITATIONS_ENDPOINT}/resend?${queryParams.toString()}`;

    try {
        const { data } = await put(url);
        return data;
    } catch (err) {
        throw err.response?.data || err;
    }
};

/**
 * Update an invitation via PATCH /invitations/update.
 *
 * Used for role updates (request_type: 'update_role'). Resending uses the
 * dedicated resendInvitation / resendLytInvitation endpoints instead.
 * The invitation is identified by three query params: agency_id, invitee_email, created_at_ts.
 *
 * @param {Object} params
 * @param {string}   params.agency_id       - Agency ID the invitation belongs to
 * @param {string}   params.invitee_email   - Email of the invitee
 * @param {string}   params.created_at_ts   - ISO timestamp when the invitation was created
 * @param {string}   params.assigned_role   - New role to assign (e.g. 'traffic_viewer')
 * @param {string[]} [params.jurisdictions] - Jurisdiction IDs (for traffic_viewer)
 * @param {string}   params.request_type    - Type of request (e.g. 'update_role')
 * @param {string}   [params.change_reason] - Reason for the change
 * @returns {Promise<Object>} - API response data
 */
export const updateInvitation = async ({
    agency_id,
    invitee_email,
    created_at_ts,
    assigned_role,
    jurisdictions,
    request_type,
    change_reason,
} = {}) => {
    const queryParams = new URLSearchParams();
    queryParams.append('agency_id', agency_id);
    queryParams.append('invitee_email', invitee_email);
    queryParams.append('created_at_ts', created_at_ts);
    const url = `/invitations/update?${queryParams.toString()}`;

    const body = {
        assigned_role,
        request_type,
    };

    const jurisdictionIds = toInvitationJurisdictionIds(jurisdictions);
    if (jurisdictionIds.length) {
        body.jurisdictions = jurisdictionIds;
    }
    if (change_reason) {
        body.change_reason = change_reason;
    }

    try {
        const { data } = await patch(url, body);
        return data;
    } catch (err) {
        throw err.response?.data || err;
    }
};

/**
 * Approve or reject an invitation via PATCH /invitations/approve.
 *
 * The invitation is identified by three query params: agency_id, invitee_email, created_at_ts.
 *
 * @param {Object} params
 * @param {string}  params.agency_id       - Agency ID the invitation belongs to
 * @param {string}  params.invitee_email   - Email of the invitee
 * @param {string}  params.created_at_ts   - ISO timestamp when the invitation was created
 * @param {boolean} params.is_approved     - true to approve, false to reject
 * @param {string}  [params.change_reason] - Reason for the decision
 * @returns {Promise<Object>} - API response data
 */
export const approveRejectInvitation = async ({
    agency_id,
    invitee_email,
    created_at_ts,
    is_approved,
    change_reason,
} = {}) => {
    const queryParams = new URLSearchParams();
    queryParams.append('agency_id', agency_id);
    queryParams.append('invitee_email', invitee_email);
    queryParams.append('created_at_ts', created_at_ts);
    const url = `/invitations/approve?${queryParams.toString()}`;

    const body = {
        is_approved,
    };

    if (change_reason) {
        body.change_reason = change_reason;
    }

    try {
        const { data } = await patch(url, body);
        return data;
    } catch (err) {
        throw err.response?.data || err;
    }
};

/**
 * Validate an invitation token before account setup.
 * Replaces legacy `GET /users/:userID/invite/:inviteToken`.
 */
export const getInvitationByToken = async ({ inviteToken, token } = {}) => {
    const invitationToken = inviteToken ?? token;
    const url = buildUrl('/invitations/accept', { token: invitationToken });

    try {
        const { data } = await get(url);
        return data;
    } catch (err) {
        throw err.response?.data || err;
    }
};

/**
 * Complete invitation registration (account setup).
 * Replaces legacy `PUT /users/:userID/invite/:inviteToken`.
 */
export const completeInvitationRegistration = async (reqData = {}, { inviteToken, token } = {}) => {
    const invitationToken = inviteToken ?? token;
    const url = buildUrl('/invitations/accept', { token: invitationToken });

    try {
        const response = await put(url, reqData);
        return response;
    } catch (err) {
        throw err.response?.data || err;
    }
};
